
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { InstagramProfile } from '@/types/instagram';
import { useToast } from '@/hooks/use-toast';

export const useInstagramProfiles = (userId?: string | null) => {
  const [profiles, setProfiles] = useState<InstagramProfile[]>([]);
  const [hasUsername, setHasUsername] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchProfiles = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!userId) {
        setProfiles([]);
        setIsLoading(false);
        return;
      }

      // First, get the user's Instagram username from their profile
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('instagram_username')
        .eq('user_id', userId)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        setProfiles([]);
        setIsLoading(false);
        return;
      }

      if (!userProfile?.instagram_username) {
        setHasUsername(false);
        setProfiles([]);
        setIsLoading(false);
        return;
      }

      setHasUsername(true);

      // Now get the Instagram metrics for this specific username
      // Use case-insensitive comparison since Instagram usernames are case-insensitive
      const { data, error: fetchError } = await supabase
        .from('instagram_daily_metrics')
        .select('*')
        .ilike('username', userProfile.instagram_username)
        .order('scraped_at', { ascending: false })
        .limit(1);

      if (fetchError) {
        throw fetchError;
      }

      
      setProfiles(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch Instagram profile';
      setError(errorMessage);
      console.error('Error in fetchProfiles:', err);
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchProfiles();
    }
  }, [userId]);

  return {
    profiles,
    hasUsername,
    isLoading,
    error,
    refetch: fetchProfiles
  };
};
